import dayjs from "dayjs"
import { formatDurationFromHours } from "../../../helpers/formatDuration"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { getUserFullName } from "../../../helpers/userDisplay"
import {
  resolveSessionCity,
  resolveSessionProject,
} from "../../../helpers/session"

/** Relations required for Admin Sessions table columns. */
export const ADMIN_SESSION_LIST_WITH = [
  "creator",
  "targets.targetable",
]

/** Relations required for Admin Session detail UI. */
export const ADMIN_SESSION_DETAIL_WITH = [
  "creator",
  "collaborators.user",
  "timeEntries",
  "targets.targetable",
]

export const SESSION_TYPE_TABS = [
  { key: "all", label: "All" },
  { key: "solo", label: "Solo" },
  { key: "shadow", label: "Shadow" },
]

export const SESSION_TYPE_OPTIONS = [
  { value: "solo", label: "Solo" },
  { value: "shadow", label: "Shadow" },
]

export const SESSION_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "ended", label: "Ended" },
]

export const SESSION_SOURCE_OPTIONS = [
  { value: "project", label: "Project" },
  { value: "task", label: "Task" },
  { value: "city", label: "City" },
  { value: "global", label: "Global" },
]

export const SESSION_VISIBILITY_OPTIONS = [
  { value: "private", label: "Private" },
  { value: "global", label: "Public" },
]

export const TYPE_LABEL = {
  solo: "Solo",
  shadow: "Shadow",
}

export const STATUS_LABEL = {
  active: "Active",
  ended: "Ended",
  completed: "Ended",
}

export const VISIBILITY_LABEL = {
  private: "Private",
  global: "Public",
  public: "Public",
}

export const SOURCE_LABEL = {
  project: "Project",
  task: "Task",
  city: "City",
  global: "Global",
}

const formatCount = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return "0"
  return n.toLocaleString("en-US")
}

const formatLoggedHours = value => {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return "0h 00m 00s"
  return formatDurationFromHours(n)
}

const formatSessionDate = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMM D, YYYY, h:mm A") : "--"
}

const creatorName = creator => {
  if (!creator) return "--"
  return getUserFullName(creator, { fallback: "--" })
}

const sessionInitials = (title, memberName) => {
  const source = String(title || memberName || "").trim()
  if (!source) return "--"
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || "")
    .join("")
}

const projectLabel = item => {
  const project = resolveSessionProject(item)
  if (project?.name) return project.name
  const city = resolveSessionCity(item)
  if (city?.name) return city.name
  const target = Array.isArray(item?.targets) ? item.targets[0] : null
  const targetable = target?.targetable
  return (
    targetable?.name ||
    targetable?.title ||
    targetable?.city ||
    String(item?.source_type || "")
      .trim()
      .replace(/_/g, " ")
      .replace(/\b\w/g, c => c.toUpperCase()) ||
    "--"
  )
}

/**
 * Map GET /api/v1/admin/sessions/summary → KPI cards.
 */
export const mapSessionsSummaryKpi = (result = {}) => [
  {
    key: "total",
    title: "Total Sessions",
    value: formatCount(
      result.total_sessions ?? result.total ?? result.sessions,
    ),
    tone: "lightblue-custom",
  },
  {
    key: "solo",
    title: "Solo Sessions",
    value: formatCount(result.solo_sessions ?? result.solo),
    tone: "lightteal",
  },
  {
    key: "shadow",
    title: "Shadow Sessions",
    value: formatCount(result.shadow_sessions ?? result.shadow),
    tone: "red",
  },
  {
    key: "active",
    title: "Active Sessions",
    value: formatCount(result.active_sessions ?? result.active),
    tone: "purple-custom",
  },
]

/**
 * Map GET /api/v1/admin/sessions item → table row.
 */
export const mapSessionRow = item => {
  const type = String(item?.type || "")
    .trim()
    .toLowerCase()
  const status = String(item?.status || "")
    .trim()
    .toLowerCase()
  const creator = item?.creator || null
  const member = creatorName(creator)
  const title = String(item?.title || item?.working_note || "").trim() || "--"

  return {
    id: item?.id,
    title,
    initials: sessionInitials(title, member),
    type: type || "solo",
    project: projectLabel(item),
    member,
    memberAvatar: getUserAvatarUrl(creator),
    loggedHours: formatLoggedHours(item?.total_working_hours),
    status: status === "completed" ? "ended" : status || "active",
    date: formatSessionDate(item?.started_at || item?.created_at),
    sourceType: String(item?.source_type || "")
      .trim()
      .toLowerCase(),
    visibilityType: String(item?.visibility_type || "")
      .trim()
      .toLowerCase(),
    raw: item,
  }
}
